<script lang="ts">
	/**
	 * Adapter that surfaces the existing `AddEditButtonsNew` room kebab in the
	 * shared sub-tabs action slot (via `$listHeader.extraActions`). The room
	 * device list also needs a "Change view" (List/Grid) affordance — instead
	 * of rendering it as a separate segmented control, we fold it into the
	 * same kebab dropdown as a dedicated group. Event dispatch on
	 * `AddEditButtonsNew` is converted to plain callback props so this
	 * component can be mounted through `svelte:component`.
	 */
	import { format } from 'svelte-i18n';
	import AddEditButtonsNew from 'src/components/AddEditButtonsNew.svelte';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { save_home } from 'src/helpers/home_helpers';
	import type { DeviceListLayoutType } from 'src/helpers/new-layout-helpers';
	import { home_data } from 'src/stores';

	export let showBulkActions = false;
	export let showDeviceListToggle = false;
	export let disableEditButton = false;
	export let disableReorderButton = false;
	export let managed = false;
	// Retained for API parity with the previous inline `DeviceListToggleLayout`
	// tooltip. Not consumed here since view selection now sits inside the kebab
	// (dropdown items already respect their `disabled` flag on shared homes).
	export const sharedHomeManagedTooltip: string | null = null;

	export let onAddClick: () => void = () => {};
	export let onEditRoomClick: () => void = () => {};
	export let onDeleteRoomClick: () => void = () => {};
	export let onManagedClick: () => void = () => {};
	export let onSelectAll: () => void = () => {};
	export let onSubmitClick: () => void = () => {};

	$: currentView = ($home_data?.['device_list']?.type as DeviceListLayoutType) || 'default';

	const onChangeDeviceListType = async (type: DeviceListLayoutType) => {
		if (managed || currentView === type) return;
		const res = await save_home('device_list', { type });
		onSettingsResponse(res);
	};

	// Build the "Change view" section only when the room list actually needs
	// a view toggle (i.e. rooms with devices, not groups/scenes/etc.). Items
	// carry their own `onClick` so `OptionManagmentIconButton` fires them
	// directly instead of routing through its event dispatch.
	$: viewSelectionGroup = showDeviceListToggle
		? {
				groupTitleDefault: 'Change view',
				groupTitle: 'label_change_view',
				isGroupTitle: true,
				items: [
					{
						id: 101,
						titleDefault: 'List view',
						title: 'label_list_view',
						icon: 'fas fa-list',
						disabled: managed || currentView === 'default',
						onClick: () => onChangeDeviceListType('default'),
					},
					{
						id: 102,
						titleDefault: 'Grid view',
						title: 'label_grid_view',
						icon: 'fas fa-grid-2',
						disabled: managed || currentView === 'compact',
						onClick: () => onChangeDeviceListType('compact'),
					},
				],
			}
		: null;
</script>

<AddEditButtonsNew
	{showBulkActions}
	{managed}
	{disableEditButton}
	{disableReorderButton}
	{viewSelectionGroup}
	triggerVariant="muted"
	on:managed_click={() => onManagedClick()}
	on:add_click={() => onAddClick()}
	on:edit_room_click={() => onEditRoomClick()}
	on:delete_room_click={() => onDeleteRoomClick()}
	on:select-all={() => onSelectAll()}
	on:submit-click={() => onSubmitClick()}
/>
